/**
 * Error Handling Utilities for API Responses
 *
 * Parses standardized API error responses and determines appropriate user actions.
 * Works with backend error format from Task #68539.
 */

import {isAbortError, getStatusBasedMessage} from './error-status';
import {ApiError} from './api-error';

/**
 * Client-side error type identifiers (not displayed to user)
 *
 * These are internal error types that don't need error ID display:
 * - NETWORK: Connectivity issues (no response from server)
 * - ABORT: User cancelled the request
 *
 * All other errors are API errors with backend-provided error IDs that SHOULD be displayed
 */
export const KNOWN_ERROR_IDS = {
    NETWORK: 'network-error',
    ABORT: 'abort'
} as const;

/**
 * Error domain identifiers for domain-scoped error storage
 * Used with errorsByDomain Map to namespace errors by feature area
 */
export const ERROR_DOMAINS = {
    ROSTERS: 'rosters',
    PROCESSING_FILES: 'processingFiles'
} as const;

export type ErrorDomain = typeof ERROR_DOMAINS[keyof typeof ERROR_DOMAINS];

/**
 * Parsed error object for frontend consumption
 *
 * Three categories of errors:
 * 1. API Errors (from backend): errorId is backend-generated string (e.g., "abc12345")
 * 2. Network Errors: errorId = "network-error", isNetworkError = true
 * 3. Abort Errors: errorId = "abort", isAbortError = true
 */
export interface ParsedApiError {
    message: string;          // User-friendly message from API (safe to display)
    errorId?: string;        // Tracking ID: backend error_id, "network-error", "abort", or undefined
    status: number;          // HTTP status code
    retryable: boolean;      // Whether user should be offered retry option
    field?: string;          // Validation field name (if applicable)
    isNetworkError: boolean; // Client-side network failure (no response)
    isAbortError: boolean;   // User cancelled the request
}

/**
 * Parse an API error and return structured error information
 *
 * Handles multiple error types:
 * 1. AbortError - User cancelled request (silent, no message)
 * 2. TypeError (fetch) - Network connectivity issues (retryable)
 * 3. ApiError - Structured API responses with error_id and details
 * 4. Legacy errors - Backwards compatible with old error format
 * 5. Unknown errors - Fallback for unexpected error types
 *
 * @param error - The caught error object (from catch block) - can be any type
 * @returns ParsedApiError with standardized fields:
 *   - message: User-friendly error message (safe to display)
 *   - errorId: Unique tracking ID for support tickets (or known constant)
 *   - status: HTTP status code (0 for network/unknown errors)
 *   - retryable: Whether user should be offered a retry action (true for 5xx errors)
 *   - field: Validation field name (for 4xx validation errors)
 *   - isNetworkError: True for fetch/connectivity failures
 *   - isAbortError: True for user-cancelled requests
 *
 * @example
 * try {
 *     await api.fetchData();
 * } catch (error: unknown) {
 *     const parsedError = parseApiError(error);
 *     if (parsedError.isAbortError) return; // Silent abort
 *     console.error(parsedError.message); // User-friendly message
 *     if (parsedError.retryable) {
 *         // Show retry button for 5xx errors
 *     }
 * }
 */
export function parseApiError(error: unknown): ParsedApiError {
    if (error instanceof Error && isAbortError(error)) {
        return {
            message: '',
            errorId: KNOWN_ERROR_IDS.ABORT,
            status: 0,
            retryable: false,
            isNetworkError: false,
            isAbortError: true
        };
    }

    if (error instanceof TypeError && error.message.toLowerCase().includes('fetch')) {
        return {
            message: 'Connection lost. Check your network and try again.',
            errorId: KNOWN_ERROR_IDS.NETWORK,
            status: 0,
            retryable: true,
            isNetworkError: true,
            isAbortError: false
        };
    }

    if (error instanceof ApiError) {
        if (error.apiError?.error) {
            // PRIORITY: Use backend message if provided, fallback to status-based only if missing
            const backendMessage = error.apiError.error.message;
            const message = backendMessage || getStatusBasedMessage(error.apiError.error.status);

            return {
                message,
                errorId: error.apiError.error.error_id,
                status: error.apiError.error.status,
                retryable: shouldRetryError(error.apiError.error.status),
                field: error.apiError.error.details?.field,
                isNetworkError: false,
                isAbortError: false
            };
        }
        // PRIORITY: Use backend message if provided, fallback to status-based only if missing
        const message = error.message?.trim() || getStatusBasedMessage(error.httpStatus);

        return {
            message,
            errorId: undefined,
            status: error.httpStatus,
            retryable: shouldRetryError(error.httpStatus),
            isNetworkError: false,
            isAbortError: false
        };
    }

    // Fallback for unexpected errors
    return {
        message: 'An unexpected error occurred. Please try again.',
        errorId: undefined,
        status: 0,
        retryable: true,
        isNetworkError: false,
        isAbortError: false
    };
}

/**
 * Check if an error ID should be displayed in toast notifications
 * @param errorId - The error ID to check
 * @returns true if the error ID should be displayed
 */
export function shouldDisplayErrorId(errorId: string | undefined): boolean {
    if (!errorId) return false;
    return !Object.values(KNOWN_ERROR_IDS).includes(errorId as any);
}

/**
 * Determine if an error is retryable based on HTTP status code
 *
 * Retry logic:
 * - 5xx server errors: YES (temporary server issues)
 * - 4xx client errors: NO (user needs to fix request)
 * - Network errors: YES (handled separately in parseApiError)
 *
 * @param status - HTTP status code
 * @returns true if error is retryable
 */
export function shouldRetryError(status: number): boolean {
    return status >= 500 && status < 600;
}
